import React from "react";
import { Link } from "react-router-dom";
import { FileText, Shield, Scale, Cookie, Users, Briefcase, Building2, ChevronRight } from "lucide-react";

const LEGAL_NAV = [
  { href: "/legal/privacy",          label: "Privacy Policy",          Icon: Shield },
  { href: "/legal/cookies",          label: "Cookie Policy",           Icon: Cookie },
  { href: "/legal/terms-buyer",      label: "Buyer Terms",             Icon: Briefcase },
  { href: "/legal/terms-publisher",  label: "Publisher Terms",         Icon: Users },
  { href: "/legal/terms-affiliate",  label: "Affiliate Terms",         Icon: Users },
  { href: "/legal/dpa",              label: "Data Processing",         Icon: FileText },
  { href: "/legal/acceptable-use",   label: "Acceptable Use",          Icon: Scale },
  { href: "/trust",                  label: "Trust Center",            Icon: Building2 },
];

/**
 * Shared layout for every legal/policy page.
 *  - sticky sidebar with sibling docs
 *  - article body with consistent typography
 *  - "last updated" line + version
 */
export default function LegalLayout({ title, eyebrow = "Legal", lastUpdated, version = "1.0", children }) {
  return (
    <div className="bg-white">
      <div className="border-b border-slate-200 bg-gradient-to-b from-slate-50/50 to-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-10">
          <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">{eyebrow}</div>
          <h1 className="mt-3 text-4xl sm:text-5xl tracking-[-0.035em] font-semibold text-slate-900 leading-[1.05]" data-testid="legal-title">
            {title}
          </h1>
          {lastUpdated && (
            <p className="mt-3 text-sm text-slate-500">
              Last updated: <span className="font-medium text-slate-700">{lastUpdated}</span>
              <span className="mx-2 text-slate-300">·</span>
              Version <span className="font-mono text-slate-700">{version}</span>
            </p>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid lg:grid-cols-[240px_1fr] gap-10">
          {/* Sidebar — sticky */}
          <aside className="lg:sticky lg:top-24 self-start" data-testid="legal-sidebar">
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500 mb-3">
              Legal documents
            </div>
            <nav className="space-y-0.5">
              {LEGAL_NAV.map(({ href, label, Icon }) => {
                const active = typeof window !== "undefined" && window.location.pathname === href;
                return (
                  <Link
                    key={href}
                    to={href}
                    className={`group flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-[13.5px] transition-colors ${
                      active
                        ? "bg-[#0055FF]/[0.08] text-[#0055FF] font-medium"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                    data-testid={`legal-nav-${href.split("/").pop()}`}
                  >
                    <span className="flex items-center gap-2.5 min-w-0">
                      <Icon className="size-3.5 flex-shrink-0" strokeWidth={2} />
                      <span className="truncate">{label}</span>
                    </span>
                    <ChevronRight className={`size-3 flex-shrink-0 transition-opacity ${active ? "opacity-100" : "opacity-0 group-hover:opacity-60"}`} />
                  </Link>
                );
              })}
            </nav>

            <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50/50 p-4">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">
                Data Protection
              </div>
              <p className="mt-2 text-[12.5px] text-slate-600 leading-relaxed">
                Questions about your data?
              </p>
              <a
                href="mailto:legal@connex.is"
                className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-[#0055FF] hover:underline"
                data-testid="legal-dpo-email"
              >
                legal@connex.is →
              </a>
            </div>
          </aside>

          {/* Article body */}
          <article
            className="prose prose-slate max-w-none
                       prose-headings:tracking-[-0.02em] prose-headings:font-semibold
                       prose-h2:text-[26px] prose-h2:mt-12 prose-h2:mb-4 prose-h2:text-slate-900
                       prose-h3:text-[18px] prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-slate-900
                       prose-p:text-[15px] prose-p:text-slate-700 prose-p:leading-[1.75]
                       prose-li:text-[15px] prose-li:text-slate-700 prose-li:leading-[1.7]
                       prose-strong:text-slate-900 prose-strong:font-semibold
                       prose-a:text-[#0055FF] prose-a:no-underline hover:prose-a:underline"
            data-testid="legal-article"
          >
            {children}
          </article>
        </div>
      </div>
    </div>
  );
}
